import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import ShareResults from '../components/ShareResults';
import { 
  ArrowLeft, 
  Users, 
  Map, 
  TrendingUp,
  BarChart3,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import Chart from 'chart.js/auto';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicResults() {
  const { surveyId } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const sentimentChartRef = useRef(null);
  const sentimentChartInstance = useRef(null);

  useEffect(() => {
    fetchAnalytics();
  }, [surveyId]);

  useEffect(() => {
    if (analytics) {
      renderChart();
    }
    return () => {
      if (sentimentChartInstance.current) sentimentChartInstance.current.destroy();
    };
  }, [analytics]);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API}/analytics/survey/${surveyId}`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        setError('Survey not found or no results available yet.');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load results.');
    } finally {
      setLoading(false);
    }
  };

  const renderChart = () => {
    if (sentimentChartRef.current && analytics?.sentiment_distribution) {
      if (sentimentChartInstance.current) sentimentChartInstance.current.destroy();
      
      const ctx = sentimentChartRef.current.getContext('2d');
      sentimentChartInstance.current = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Positive', 'Neutral', 'Negative'],
          datasets: [{
            data: [
              analytics.sentiment_distribution.positive || 0,
              analytics.sentiment_distribution.neutral || 0,
              analytics.sentiment_distribution.negative || 0
            ],
            backgroundColor: ['#059669', '#6B7280', '#DC2626'],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                padding: 20,
                usePointStyle: true
              }
            }
          },
          cutout: '60%'
        }
      });
    }
  };

  const getDominantSentiment = () => {
    if (!analytics?.sentiment_distribution) return 'neutral';
    const { positive = 0, neutral = 0, negative = 0 } = analytics.sentiment_distribution;
    if (positive >= neutral && positive >= negative) return 'positive';
    if (negative >= neutral && negative >= positive) return 'negative';
    return 'neutral';
  };

  const getTopConcern = () => {
    if (!analytics?.question_analytics) return '';
    for (const qId in analytics.question_analytics) {
      const q = analytics.question_analytics[qId];
      if (q.type === 'multiple_choice' && q.options?.length > 0) {
        const sorted = [...q.options].sort((a, b) => b.count - a.count);
        if (sorted[0]?.text) return sorted[0].text;
      }
    }
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Results Unavailable</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button>Return Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-results">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Home
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
                  <span className="text-white font-bold text-xs">CV</span>
                </div>
                <span className="font-semibold text-sm hidden sm:inline">Citizen Voice Nigeria</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ShareResults 
                surveyId={surveyId}
                surveyTitle={analytics?.survey?.title}
                totalResponses={analytics?.total_responses}
                dominantSentiment={getDominantSentiment()}
                topConcern={getTopConcern()}
              />
              <Link to={`/survey/${surveyId}`}>
                <Button size="sm">
                  Take Survey
                  <ExternalLink className="ml-2 h-3 w-3" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Survey Title */}
        <div className="mb-8 text-center">
          <Badge variant="secondary" className="mb-4">
            {analytics?.survey?.policy_category} • {analytics?.survey?.governance_level}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            {analytics?.survey?.title}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {analytics?.survey?.description}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6 text-center">
              <Users className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="text-3xl font-bold">{analytics?.total_responses || 0}</p>
              <p className="text-sm text-muted-foreground">Responses</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Map className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="text-3xl font-bold">
                {Object.keys(analytics?.state_breakdown || {}).length}
              </p>
              <p className="text-sm text-muted-foreground">States</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <BarChart3 className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="text-3xl font-bold">{analytics?.survey?.questions?.length || 0}</p>
              <p className="text-sm text-muted-foreground">Questions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <TrendingUp className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="text-3xl font-bold capitalize">{getDominantSentiment()}</p>
              <p className="text-sm text-muted-foreground">Sentiment</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sentiment Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Citizen Sentiment</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <canvas ref={sentimentChartRef}></canvas>
              </div>
            </CardContent>
          </Card>

          {/* Question Results */}
          <div className="lg:col-span-2 space-y-6">
            {analytics?.survey?.questions?.map((question, qIndex) => {
              const qAnalytics = analytics.question_analytics?.[question.question_id];
              if (!qAnalytics) return null;

              return (
                <Card key={question.question_id}>
                  <CardHeader>
                    <div className="flex items-start gap-2">
                      <Badge variant="outline" className="mt-0.5">Q{qIndex + 1}</Badge>
                      <div>
                        <CardTitle className="text-base">{question.text}</CardTitle>
                        <CardDescription className="capitalize">
                          {question.question_type.replace('_', ' ')}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {question.question_type === 'multiple_choice' && qAnalytics.options && (
                      <div className="space-y-3">
                        {qAnalytics.options
                          .sort((a, b) => b.count - a.count)
                          .map((opt) => (
                          <div key={opt.option_id}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="truncate max-w-[70%]">{opt.text}</span>
                              <span className="font-mono text-muted-foreground">
                                {opt.count} ({opt.percentage}%)
                              </span>
                            </div>
                            <Progress value={opt.percentage} className="h-2" />
                          </div>
                        ))}
                      </div>
                    )}

                    {question.question_type === 'rating' && qAnalytics.distribution && (
                      <div>
                        <div className="text-center mb-4">
                          <span className="text-4xl font-bold text-primary">
                            {qAnalytics.average}
                          </span>
                          <span className="text-lg text-muted-foreground">/5</span>
                          <p className="text-sm text-muted-foreground mt-1">
                            Average rating from {qAnalytics.total} responses
                          </p>
                        </div>
                        <div className="flex items-end justify-center gap-2 h-24">
                          {[1, 2, 3, 4, 5].map((rating) => {
                            const count = qAnalytics.distribution[rating] || 0;
                            const maxCount = Math.max(...Object.values(qAnalytics.distribution));
                            const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                            return (
                              <div key={rating} className="flex flex-col items-center">
                                <div 
                                  className="w-10 bg-primary rounded-t transition-all"
                                  style={{ height: `${height}%`, minHeight: count > 0 ? '8px' : '0' }}
                                ></div>
                                <span className="text-sm font-medium mt-1">{rating}</span>
                                <span className="text-xs text-muted-foreground">{count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {question.question_type === 'open_ended' && qAnalytics.responses && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-3">
                          {qAnalytics.total} text responses received
                        </p>
                        <div className="space-y-2 max-h-48 overflow-y-auto thin-scrollbar">
                          {qAnalytics.responses.slice(0, 5).map((resp, idx) => (
                            <div key={idx} className="p-2 bg-muted/30 rounded text-sm">
                              <p className="italic">"{resp.text}"</p>
                              <span className="text-xs text-muted-foreground">- {resp.state}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Top States */}
        {analytics?.state_breakdown && Object.keys(analytics.state_breakdown).length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Top Responding States</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {Object.entries(analytics.state_breakdown)
                  .sort((a, b) => b[1].count - a[1].count)
                  .slice(0, 10)
                  .map(([state, data], idx) => (
                    <div key={state} className="p-3 bg-muted/30 rounded-lg text-center">
                      <span className="w-6 h-6 bg-primary/10 rounded-full inline-flex items-center justify-center text-xs font-bold text-primary mb-2">
                        {idx + 1}
                      </span>
                      <p className="font-medium text-sm">{state}</p>
                      <p className="text-xs text-muted-foreground">{data.count} responses</p>
                    </div>
                  ))
                }
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-12 text-center py-8 border-t">
          <p className="text-sm text-muted-foreground mb-4">
            This data represents anonymous citizen sentiment and is not official election data.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to={`/survey/${surveyId}`}>
              <Button>Participate in Survey</Button>
            </Link>
            <Link to="/analytics">
              <Button variant="outline">View All Results</Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
